import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import SellerLayout from "@/components/layouts/SellerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  CheckCircle,
  Clock,
  ShoppingCart,
  TrendingUp,
  Plus,
  AlertCircle,
  ExternalLink,
  Store,
  Crown,
} from "lucide-react";
import { toast } from "sonner";

const SellerDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/seller/dashboard`, {
        headers: getAuthHeaders(),
      });
      setDashboard(res.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <SellerLayout title="Dashboard">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
                <div className="h-8 bg-gray-200 rounded w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout title="Dashboard">
      {/* Pending Approval Notice */}
      {!dashboard?.is_approved && (
        <Card className="border-0 shadow-sm bg-yellow-50 border-yellow-200 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">
                  Account Pending Approval
                </p>
                <p className="text-sm text-yellow-600">
                  Your seller account is under review. You'll be notified once approved.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Products</p>
                <p className="text-2xl font-bold text-gray-900 font-heading">
                  {dashboard?.total_products || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Approved</p>
                <p className="text-2xl font-bold text-green-600 font-heading">
                  {dashboard?.approved_products || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-600 font-heading">
                  {dashboard?.pending_products || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Orders</p>
                <p className="text-2xl font-bold text-purple-600 font-heading">
                  {dashboard?.total_orders || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/seller/products">
              <Button
                className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400 justify-between"
                disabled={!dashboard?.is_approved}
                data-testid="add-product-btn"
              >
                <span className="flex items-center">
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Product
                </span>
                {!dashboard?.is_approved && (
                  <Badge className="bg-yellow-100 text-yellow-700">
                    Approval Required
                  </Badge>
                )}
              </Button>
            </Link>
            <Link to="/seller/products">
              <Button variant="outline" className="w-full justify-between">
                <span className="flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  Manage Products
                </span>
              </Button>
            </Link>
            <Link to="/seller/store-settings">
              <Button variant="outline" className="w-full justify-between" data-testid="dashboard-store-builder-btn">
                <span className="flex items-center">
                  <Store className="w-4 h-4 mr-2" />
                  Design My Store
                </span>
              </Button>
            </Link>
            <Link to="/seller/plans">
              <Button variant="outline" className="w-full justify-between" data-testid="dashboard-plans-btn">
                <span className="flex items-center">
                  <Crown className="w-4 h-4 mr-2" />
                  View Plans & Upgrade
                </span>
                {dashboard?.plan_name && <Badge className="bg-secondary-100 text-secondary-700" data-testid="dashboard-plan-badge">{dashboard.plan_name}</Badge>}
              </Button>
            </Link>
            {dashboard?.store_slug && (
              <Link to={`/store/${dashboard.store_slug}`} target="_blank">
                <Button variant="outline" className="w-full justify-between">
                  <span className="flex items-center">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View My Store
                  </span>
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>

        {/* Store Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Store Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Store Status</span>
                <Badge
                  className={
                    dashboard?.is_approved
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }
                >
                  {dashboard?.is_approved ? "Active" : "Pending"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Store URL</span>
                <span className="font-mono text-sm text-primary-600">
                  /store/{dashboard?.store_slug}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Business Name</span>
                <span className="font-medium">{user?.business_name}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </SellerLayout>
  );
};

export default SellerDashboard;
